import { LitElement, css, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import receiptImage from '../../../assets/images/recibo.jpg';
import {
  type CreateOrderInput,
  type OrderCreator,
  type OrderPaymentConfirmer,
} from '../../../domain/order/order';

type PaymentTab = 'register' | 'buy' | 'donate';
type PaymentFlowStage =
  | 'form'
  | 'creating-order'
  | 'payment'
  | 'confirming-payment'
  | 'completed';

@customElement('rftw-payment-popup')
export class RftwPaymentPopup extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: String }) activeTab: PaymentTab = 'register';
  @property({ type: String }) orderId = '';
  @property({ type: String }) paymentUrl = '';
  @property({ attribute: false }) orderCreator?: OrderCreator;
  @property({ attribute: false }) orderPaymentConfirmer?: OrderPaymentConfirmer;
  @state() private submittedTab: PaymentTab | null = null;
  @state() private paymentStage: PaymentFlowStage = 'form';
  @state() private orderError = '';
  @state() private paymentConfirmationError = '';
  @state() private buyShirtType: 'jersey' | 'tshirt' | '' = '';

  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: none;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    :host([open]) {
      display: grid;
      place-items: center;
      padding: 1.25rem;
      box-sizing: border-box;
    }

    .backdrop {
      position: absolute;
      inset: 0;
      background: rgba(27, 46, 35, 0.72);
    }

    .dialog {
      position: relative;
      width: min(100%, 620px);
      max-height: min(720px, calc(100vh - 2.5rem));
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: #ffffff;
      border-radius: 8px;
      box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28);
      border-top: 4px solid var(--wa-color-orange-50, #d95328);
    }

    .dialog-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.5rem 1.5rem 1.1rem;
    }

    .eyebrow {
      margin: 0 0 0.4rem;
      color: var(--wa-color-brand, #ff4f0b);
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      line-height: 1.2;
    }

    h2 {
      margin: 0;
      color: #1b2e23;
      font-size: 1.8rem;
      font-weight: 800;
      line-height: 1.2;
    }

    .close-button {
      display: grid;
      place-items: center;
      width: 2.25rem;
      height: 2.25rem;
      flex: 0 0 auto;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: #1b2e23;
      cursor: pointer;
      font-size: 1.5rem;
      line-height: 1;
    }

    .tabs {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      margin: 0 1.5rem;
      border-bottom: 1px solid #d8d6cf;
      flex: 0 0 auto;
    }

    .tab {
      min-height: 3rem;
      padding: 0.75rem 0.35rem 0.65rem;
      border: 0;
      border-bottom: 3px solid transparent;
      background: transparent;
      color: #5b625c;
      cursor: pointer;
      font: inherit;
      font-size: 0.82rem;
      font-weight: 700;
    }

    .tab[aria-selected='true'] {
      border-bottom-color: var(--wa-color-orange-50, #d95328);
      color: #1b2e23;
    }

    .panel {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      padding: 1.5rem;
      background: #ffffff;
      scrollbar-color: #bfc5bf transparent;
    }

    .intro {
      margin: 0 0 1.25rem;
      color: #4a5568;
      font-size: 0.94rem;
      line-height: 1.55;
    }

    form {
      display: grid;
      gap: 1rem;
    }

    .form-actions {
      position: sticky;
      bottom: -1.5rem;
      margin: 0 -1.5rem -1.5rem;
      padding: 1rem 1.5rem 1.5rem;
      background: #ffffff;
      box-shadow: 0 -0.75rem 1rem rgba(255, 255, 255, 0.96);
      z-index: 2;
    }

    .form-actions .primary-button {
      width: 100%;
    }

    .field {
      display: grid;
      gap: 0.4rem;
    }

    label {
      color: #1b2e23;
      font-size: 0.84rem;
      font-weight: 700;
    }

    input,
    select {
      width: 100%;
      min-height: 2.8rem;
      border: 1px solid #bfc5bf;
      border-radius: 6px;
      background: #ffffff;
      color: #1b2e23;
      box-sizing: border-box;
      font: inherit;
      padding: 0.65rem 0.75rem;
    }

    input:focus-visible,
    select:focus-visible,
    .close-button:focus-visible,
    .tab:focus-visible,
    .primary-button:focus-visible,
    .payment-link:focus-visible {
      outline: 3px solid rgba(217, 83, 40, 0.35);
      outline-offset: 2px;
    }

    .primary-button {
      min-height: 3.25rem;
      border: 0;
      border-radius: 6px;
      background: var(--wa-color-orange-50, #d95328);
      color: #ffffff;
      cursor: pointer;
      font: inherit;
      font-size: 0.94rem;
      font-weight: 700;
    }

    .primary-button:hover {
      background: #b94220;
    }

    .sinpe-details {
      padding: 1.25rem;
      border-left: 4px solid var(--wa-color-orange-50, #d95328);
      background: #ffffff;
      color: #1b2e23;
    }

    .sinpe-details p {
      margin: 0.35rem 0;
      line-height: 1.5;
    }

    .sinpe-details strong {
      font-size: 1.15rem;
    }

    .confirmation {
      padding: 1rem;
      border-radius: 6px;
      background: #e5efe4;
      color: #1b2e23;
      line-height: 1.5;
    }

    .payment-step {
      display: grid;
      gap: 1rem;
    }

    .payment-step h3 {
      margin: 0;
      color: #1b2e23;
      font-size: 1.35rem;
      font-weight: 800;
    }

    .payment-step p {
      margin: 0;
      color: #4a5568;
      line-height: 1.55;
    }

    .payment-step ol {
      margin: 0;
      padding-left: 1.35rem;
      color: #4a5568;
      line-height: 1.6;
    }

    .payment-notice {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.8rem;
      align-items: start;
      padding: 0.9rem 1rem;
      border: 1px solid #4f956d;
      border-radius: 6px;
      background: #e8f1e7;
      color: #34463a;
    }

    .payment-notice-icon {
      display: grid;
      place-items: center;
      width: 1.35rem;
      height: 1.35rem;
      margin-top: 0.1rem;
      border: 2px solid #28643e;
      border-radius: 50%;
      color: #28643e;
      font-size: 0.85rem;
      font-weight: 800;
      line-height: 1;
    }

    .payment-notice strong,
    .payment-notice p {
      display: block;
    }

    .payment-notice p {
      margin: 0.15rem 0 0;
    }

    .payment-instructions {
      margin: 0;
      padding-left: 1.45rem;
    }

    .receipt-image {
      display: block;
      width: min(100%, 390px);
      margin: 0 auto;
      border: 1px solid #d8d6cf;
    }

    .payment-confirmation-form {
      gap: 0;
    }

    .payment-confirmation-actions {
      position: sticky;
      bottom: -1.5rem;
      margin: 1.25rem -1.5rem -1.5rem;
      padding: 1rem 1.5rem 1.5rem;
      background: #ffffff;
      box-shadow: 0 -0.75rem 1rem rgba(255, 255, 255, 0.96);
    }

    .payment-confirmation-actions .primary-button {
      width: 100%;
    }

    .payment-link {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 3rem;
      border-radius: 6px;
      background: var(--wa-color-orange-50, #d95328);
      color: #ffffff;
      font: inherit;
      font-weight: 700;
      text-decoration: none;
      width: 100%;
      box-sizing: border-box;
      cursor: pointer;
      border: 0;
    }

    .order-error {
      margin: 0;
      padding: 0.85rem 1rem;
      border-left: 4px solid #b94220;
      background: #fff0eb;
      color: #8e2f18;
      line-height: 1.5;
    }

    @media (max-width: 480px) {
      :host([open]) {
        padding: 0.75rem;
      }

      .dialog-header,
      .panel {
        padding: 1.25rem;
      }

      .tabs {
        margin: 0 1.25rem;
      }

      .tab {
        font-size: 0.72rem;
      }

      .form-actions {
        margin: 0 -1.25rem -1.25rem;
        padding: 1rem 1.25rem 1.25rem;
      }

      .payment-confirmation-actions {
        bottom: -1.25rem;
        margin: 1.25rem -1.25rem -1.25rem;
        padding: 1rem 1.25rem 1.25rem;
      }
    }
  `;

  private _close() {
    this.open = false;
    this.submittedTab = null;
    this.paymentStage = 'form';
    this.orderId = '';
    this.paymentUrl = '';
    this.orderError = '';
    this.paymentConfirmationError = '';
    this.buyShirtType = '';
    this.dispatchEvent(new CustomEvent('payment-popup-close'));
  }

  private _selectTab(tab: PaymentTab) {
    this.activeTab = tab;
    this.submittedTab = null;
    this.paymentStage = 'form';
    this.orderError = '';
    this.paymentConfirmationError = '';
    this.buyShirtType = '';
  }

  protected updated(changedProperties: PropertyValues) {
    if (
      (changedProperties.has('orderId') ||
        changedProperties.has('paymentUrl')) &&
      this.paymentStage === 'creating-order' &&
      this.orderId &&
      this.paymentUrl
    ) {
      this.paymentStage = 'payment';
    }
  }

  private async _submitOrder(
    event: SubmitEvent,
    tab: 'register' | 'buy' | 'donate'
  ) {
    event.preventDefault();
    this.paymentStage = 'creating-order';
    this.orderError = '';

    if (!this.orderCreator) {
      this.paymentStage = 'form';
      this.orderError =
        'El servicio de órdenes no está disponible. Intenta nuevamente.';
      return;
    }

    const form = new FormData(event.currentTarget as HTMLFormElement);
    const selectedShirtType =
      (form.get('shirtType') as 'jersey' | 'tshirt' | null) ?? undefined;
    const buyShirtType = (selectedShirtType ?? this.buyShirtType) || 'tshirt';
    const amount =
      tab === 'register'
        ? 50
        : tab === 'buy'
          ? buyShirtType === 'jersey'
            ? 40
            : 30
          : Number(form.get('amount') ?? 0);

    const jerseyName =
      tab === 'register'
        ? String(form.get('jerseyNickname') ?? '')
        : tab === 'buy' && buyShirtType === 'jersey'
          ? String(form.get('jerseyName') ?? '')
          : 'N/A';

    const jerseyNumber =
      tab === 'register'
        ? String(form.get('riderNumber') ?? '')
        : tab === 'buy' && buyShirtType === 'jersey'
          ? String(form.get('jerseyNumber') ?? '')
          : 'N/A';

    const input: CreateOrderInput = {
      kind: tab,
      email: String(form.get('email') ?? ''),
      phone: String(form.get('phone') ?? ''),
      size: String(form.get('size') ?? ''),
      amount,
      shirtType: tab === 'buy' ? buyShirtType : undefined,
      jerseyName,
      jerseyNumber,
    };

    try {
      const createdOrder = await this.orderCreator.createOrder(input);
      this.orderId = createdOrder.id;
      this.paymentUrl = createdOrder.paymentUrl;
      this.paymentStage = 'payment';
    } catch (error) {
      this.paymentStage = 'form';
      this.orderError =
        error instanceof Error
          ? error.message
          : 'No pudimos crear la orden. Intenta nuevamente.';
    }
  }

  private _openPayment(event: MouseEvent) {
    event.preventDefault();
    window.open(this.paymentUrl, '_blank', 'noopener,noreferrer');
    this.paymentStage = 'confirming-payment';
  }

  private async _submitPaymentConfirmation(event: SubmitEvent) {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const confirmationNumber = String(
      form.get('paymentConfirmation') ?? ''
    ).trim();

    this.paymentConfirmationError = '';

    if (!this.orderPaymentConfirmer) {
      this.paymentConfirmationError =
        'El servicio de pagos no está disponible. Intenta nuevamente.';
      return;
    }

    try {
      await this.orderPaymentConfirmer.updateOrderConfirmationId(
        this.orderId,
        confirmationNumber
      );

      this.dispatchEvent(
        new CustomEvent('payment-confirm', {
          bubbles: true,
          composed: true,
          detail: {
            orderId: this.orderId,
            confirmationNumber,
          },
        })
      );
      this.paymentStage = 'completed';
    } catch (error) {
      this.paymentConfirmationError =
        error instanceof Error
          ? error.message
          : 'No pudimos vincular la confirmación con tu orden. Intenta nuevamente.';
    }
  }

  private _renderOrderCreating() {
    return html`
      <div class="payment-step" aria-live="polite">
        <h3>Estamos creando tu orden</h3>
        <p>
          Estamos guardando tus datos para reservar tu inscripción o tu prenda.
          Cuando la orden esté lista, podrás continuar al pago.
        </p>
      </div>
    `;
  }

  private _renderPaymentStep() {
    const isRegistration = this.activeTab === 'register';
    const isDonation = this.activeTab === 'donate';

    return html`
      <div class="payment-step">
        <h3>
          ${isRegistration
            ? 'Hemos recibido tu inscripción'
            : isDonation
              ? 'Hemos recibido tu donación'
              : 'Hemos recibido tu orden'}
        </h3>
        <p>
          ${isRegistration ? 'Inscripción' : isDonation ? 'Donación' : 'Orden'}
          <strong>${this.orderId}</strong>. Completa el pago en la nueva pestaña
          y luego vuelve aquí para registrar el número de confirmación.
        </p>
        <ol>
          <li>Abre la plataforma de pago.</li>
          <li>Finaliza el pago usando el monto de tu orden.</li>
          <li>Regresa a esta pestaña para confirmar el pago.</li>
        </ol>
        <button class="payment-link" type="button" @click=${this._openPayment}>
          Continuar
        </button>
      </div>
    `;
  }

  private _renderPaymentConfirmation() {
    return html`
      <div class="payment-step">
        <h3>Confirma tu pago</h3>
        <div class="payment-notice">
          <span class="payment-notice-icon" aria-hidden="true">!</span>
          <div>
            <strong>Importante</strong>
            <p>Debes seguir los siguientes pasos para confirmar tu pago.</p>
          </div>
        </div>
        <ol class="payment-instructions">
          <li>Ve a tu correo electrónico.</li>
          <li>
            Busca el correo de parte de MiPOS BAC Credomatic - ELEPHANT MOTO.
          </li>
          <li>
            Dentro de ese correo encontrarás un número de recibo; agrégalo aquí
            abajo.
          </li>
        </ol>
        <img
          class="receipt-image"
          src=${receiptImage}
          alt="Ejemplo del número de recibo de MiPOS BAC"
        />
        <form
          class="payment-confirmation-form"
          @submit=${this._submitPaymentConfirmation}
        >
          <div class="field">
            <label for="payment-confirmation">Número de recibo</label>
            <input
              id="payment-confirmation"
              name="paymentConfirmation"
              placeholder="Ingrese número de recibo"
              autocomplete="off"
              required
            />
          </div>
          <div class="payment-confirmation-actions">
            <button class="primary-button" type="submit">Confirmar pago</button>
            ${this.paymentConfirmationError
              ? html`<p class="order-error" role="alert">
                  ${this.paymentConfirmationError}
                </p>`
              : nothing}
          </div>
        </form>
      </div>
    `;
  }

  private _renderCompleted() {
    return html`
      <p class="confirmation" aria-live="polite">
        Gracias. Tu pago quedó asociado a la orden
        <strong>${this.orderId}</strong>. Te enviaremos la confirmación por
        correo.
      </p>
    `;
  }

  private _renderRegistrationForm() {
    return html`
      <p class="intro">
        Completa tus datos para iniciar tu inscripción al ride del 14-15 de
        Noviembre.
      </p>
      <form
        @submit=${(event: SubmitEvent) => this._submitOrder(event, 'register')}
      >
        <div class="field">
          <label for="registration-email">Correo electrónico</label
          ><input
            id="registration-email"
            name="email"
            type="email"
            autocomplete="email"
            required
          />
        </div>
        <div class="field">
          <label for="registration-phone">Número de teléfono</label
          ><input
            id="registration-phone"
            name="phone"
            type="tel"
            autocomplete="tel"
            required
          />
        </div>
        <div class="field">
          <label for="jersey-nickname">Apodo para el jersey</label
          ><input
            id="jersey-nickname"
            name="jerseyNickname"
            maxlength="20"
            required
          />
        </div>
        <div class="field">
          <label for="rider-number">Número en la jersey</label
          ><input
            id="rider-number"
            name="riderNumber"
            type="number"
            min="1"
            max="999"
            required
          />
        </div>
        <div class="field">
          <label for="tshirt-size">Talla de camiseta</label
          ><select id="tshirt-size" name="size" required>
            <option value="" selected disabled>Selecciona una talla</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL</option>
          </select>
        </div>

        <div class="form-actions">
          <button class="primary-button" type="submit">Continuar</button>
          ${this.orderError
            ? html`<p class="order-error" role="alert">${this.orderError}</p>`
            : nothing}
        </div>
      </form>
    `;
  }

  private _renderTshirtForm() {
    const isJersey = this.buyShirtType === 'jersey';

    return html`
      <p class="intro">
        Déjanos tus datos para elegir tu prenda y apoyar a IAR Nosara.
      </p>
      <form @submit=${(event: SubmitEvent) => this._submitOrder(event, 'buy')}>
        <div class="field">
          <label for="tshirt-email">Correo electrónico</label
          ><input
            id="tshirt-email"
            name="email"
            type="email"
            autocomplete="email"
            required
          />
        </div>
        <div class="field">
          <label for="tshirt-phone">Número de teléfono</label
          ><input
            id="tshirt-phone"
            name="phone"
            type="tel"
            autocomplete="tel"
            required
          />
        </div>
        <div class="field">
          <label for="shirt-type">Tipo de prenda</label
          ><select
            id="shirt-type"
            name="shirtType"
            required
            .value=${this.buyShirtType}
            @change=${(event: Event) => {
              const value = (event.currentTarget as HTMLSelectElement).value;
              this.buyShirtType =
                value === 'jersey' || value === 'tshirt' ? value : '';
            }}
          >
            <option value="" disabled>Selecciona una prenda</option>
            <option value="jersey">Jersey - $40</option>
            <option value="tshirt">Camiseta casual - $30</option>
          </select>
        </div>
        ${isJersey
          ? html`
              <div class="field">
                <label for="buy-jersey-name">Nombre para el jersey</label>
                <input
                  id="buy-jersey-name"
                  name="jerseyName"
                  maxlength="20"
                  required
                />
              </div>
              <div class="field">
                <label for="buy-jersey-number">Número en el jersey</label>
                <input
                  id="buy-jersey-number"
                  name="jerseyNumber"
                  type="number"
                  min="1"
                  max="999"
                  required
                />
              </div>
            `
          : nothing}
        <div class="field">
          <label for="tshirt-size">Talla</label
          ><select id="tshirt-size" name="size" required>
            <option value="" selected disabled>Selecciona una talla</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL</option>
          </select>
        </div>
        <div class="form-actions">
          <button class="primary-button" type="submit">Continuar</button>
          ${this.orderError
            ? html`<p class="order-error" role="alert">${this.orderError}</p>`
            : nothing}
        </div>
      </form>
    `;
  }

  private _renderDonation() {
    return html`
      <p class="intro">
        Elige el monto que deseas donar. Tu aporte llega directamente al cuidado
        de la vida silvestre en IAR Nosara.
      </p>
      <form
        @submit=${(event: SubmitEvent) => this._submitOrder(event, 'donate')}
      >
        <div class="field">
          <label for="donation-email">Correo electrónico</label>
          <input
            id="donation-email"
            name="email"
            type="email"
            autocomplete="email"
            required
          />
        </div>
        <div class="field">
          <label for="donation-phone">Número de teléfono</label>
          <input
            id="donation-phone"
            name="phone"
            type="tel"
            autocomplete="tel"
            required
          />
        </div>
        <div class="field">
          <label for="donation-amount">Monto de donación</label>
          <select id="donation-amount" name="amount" required>
            <option value="" selected disabled>Selecciona un monto</option>
            <option value="10">$10</option>
            <option value="25">$25</option>
            <option value="50">$50</option>
            <option value="100">$100</option>
          </select>
        </div>
        <div class="form-actions">
          <button class="primary-button" type="submit">Continuar</button>
          ${this.orderError
            ? html`<p class="order-error" role="alert">${this.orderError}</p>`
            : nothing}
        </div>
      </form>
    `;
  }

  render() {
    if (!this.open) return nothing;

    const content = this.submittedTab
      ? html`<p class="confirmation">
          Gracias. Recibimos tus datos y te contactaremos pronto para continuar
          con el pago.
        </p>`
      : this.paymentStage === 'creating-order'
        ? this._renderOrderCreating()
        : this.paymentStage === 'payment'
          ? this._renderPaymentStep()
          : this.paymentStage === 'confirming-payment'
            ? this._renderPaymentConfirmation()
            : this.paymentStage === 'completed'
              ? this._renderCompleted()
              : this.activeTab === 'register'
                ? this._renderRegistrationForm()
                : this.activeTab === 'buy'
                  ? this._renderTshirtForm()
                  : this._renderDonation();

    return html`
      <div class="backdrop" @click=${this._close}></div>
      <section
        class="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-title"
      >
        <header class="dialog-header">
          <div>
            <p class="eyebrow">RIDE FOR THE WILD</p>
            <h2 id="payment-title">Apoya a IAR Nosara</h2>
          </div>
          <button
            class="close-button"
            type="button"
            aria-label="Cerrar"
            @click=${this._close}
          >
            &times;
          </button>
        </header>
        <div class="tabs" role="tablist" aria-label="Opciones de participación">
          <button
            class="tab"
            role="tab"
            aria-selected=${this.activeTab === 'register'}
            @click=${() => this._selectTab('register')}
          >
            Inscripción
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected=${this.activeTab === 'buy'}
            @click=${() => this._selectTab('buy')}
          >
            Camisetas
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected=${this.activeTab === 'donate'}
            @click=${() => this._selectTab('donate')}
          >
            Donación
          </button>
        </div>
        <div class="panel" role="tabpanel">${content}</div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-payment-popup': RftwPaymentPopup;
  }
}

